import { clsx } from "clsx";
import { Lock, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Drawer, Input } from "@/components/common";
import { useCreateRole, usePermissions, useUpdateRole } from "@/hooks/useAdmin";
import type { PermissionGroup, PermissionString, Role } from "@/types/rbac";

type DrawerMode =
  | { type: "create" }
  | { type: "edit"; role: Role }
  | { type: "view"; role: Role };

interface RoleDrawerProps {
  mode: DrawerMode | null;
  onClose: () => void;
}

const actionLabels: Record<string, string> = {
  read: "Odczyt",
  create: "Tworzenie",
  update: "Edycja",
  delete: "Usuwanie",
};

const actions = ["read", "create", "update", "delete"] as const;

export function RoleDrawer({ mode, onClose }: RoleDrawerProps) {
  const { data: permissionGroups = [] } = usePermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<PermissionString[]>([]);

  const isView = mode?.type === "view";
  const isEdit = mode?.type === "edit";
  const isCreate = mode?.type === "create";
  const role = mode && mode.type !== "create" ? mode.role : null;

  const mutation = isEdit ? updateRole : createRole;

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description);
      setPermissions([...role.permissions]);
    } else if (isCreate) {
      setName("");
      setDescription("");
      setPermissions([]);
    }
  }, [role, isCreate]);

  function handleClose() {
    createRole.reset();
    updateRole.reset();
    setName("");
    setDescription("");
    setPermissions([]);
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isCreate) {
      createRole.mutate(
        { name, description, permissions },
        { onSuccess: handleClose },
      );
    } else if (isEdit && role) {
      updateRole.mutate(
        { roleId: role.id, payload: { name, description, permissions } },
        { onSuccess: handleClose },
      );
    }
  }

  const title = isCreate
    ? "Nowa rola"
    : isEdit
      ? "Edytuj rolę"
      : `Podgląd: ${role?.name ?? ""}`;

  const subtitle = isCreate
    ? "Zdefiniuj nazwę, opis i uprawnienia nowej roli"
    : isEdit
      ? "Zmień nazwę, opis lub uprawnienia roli"
      : "Role systemowe nie mogą być edytowane";

  return (
    <Drawer
      isOpen={mode !== null}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      footer={
        isView ? undefined : (
          <form
            id="role-drawer-form"
            onSubmit={handleSubmit}
            className="flex items-center justify-between"
          >
            <p className="text-xs text-dim">
              {permissions.length === 0
                ? "Zaznacz uprawnienia, które chcesz przypisać do tej roli"
                : `${permissions.length} uprawnień zaznaczonych`}
            </p>
            <div className="flex gap-3">
              <Button variant="ghost" type="button" onClick={handleClose}>
                Anuluj
              </Button>
              <Button
                type="submit"
                isLoading={mutation.isPending}
                disabled={!name || permissions.length === 0}
              >
                {isCreate ? "Utwórz rolę" : "Zapisz zmiany"}
              </Button>
            </div>
          </form>
        )
      }
    >
      <div className="px-6 py-5">
        {/* Error */}
        {mutation.isError && (
          <Alert variant="error" className="mb-5">
            {(mutation.error as { message?: string })?.message ??
              "Nie udało się zapisać roli"}
          </Alert>
        )}

        {/* System role badge */}
        {isView && (
          <div className="mb-5 flex items-center gap-2 rounded-lg bg-adara-50 px-3 py-2.5 dark:bg-adara-900/30">
            <Lock size={14} className="text-adara-700" aria-hidden="true" />
            <span className="text-sm font-medium text-adara-800">
              Rola systemowa
            </span>
            <span className="text-sm text-adara-600">
              &mdash; uprawnienia są stałe
            </span>
          </div>
        )}

        {/* Role details */}
        <fieldset disabled={isView} className="space-y-4">
          <Input
            label="Nazwa roli"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
            required
            placeholder="np. Analityk"
            maxLength={50}
          />

          <Input
            label="Opis"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            placeholder="Krótki opis uprawnień i odpowiedzialności..."
          />
        </fieldset>
      </div>

      {/* Permissions */}
      <div className="border-t border-edge-soft px-6 py-5">
        <div className="mb-4 flex items-center gap-2">
          <Shield size={16} className="text-faint" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-main">Uprawnienia</h3>
        </div>

        <PermissionsGrid
          permissionGroups={permissionGroups}
          selected={permissions}
          onChange={setPermissions}
          disabled={isView}
        />
      </div>
    </Drawer>
  );
}

// --- Permissions Grid (visual upgrade from flat table) ---

interface PermissionsGridProps {
  permissionGroups: PermissionGroup[];
  selected: PermissionString[];
  onChange: (permissions: PermissionString[]) => void;
  disabled: boolean;
}

function PermissionsGrid({
  permissionGroups,
  selected,
  onChange,
  disabled,
}: PermissionsGridProps) {
  const selectedSet = new Set(selected);

  function toggle(permId: PermissionString) {
    if (disabled) {
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(permId)) {
      next.delete(permId);
    } else {
      next.add(permId);
    }
    onChange([...next] as PermissionString[]);
  }

  function toggleRow(group: PermissionGroup) {
    if (disabled) {
      return;
    }
    const groupPerms = group.permissions.map((p) => p.id);
    const allSelected = groupPerms.every((p) => selectedSet.has(p));
    const next = new Set(selectedSet);
    for (const perm of groupPerms) {
      if (allSelected) {
        next.delete(perm);
      } else {
        next.add(perm);
      }
    }
    onChange([...next] as PermissionString[]);
  }

  return (
    <div className="space-y-2">
      {permissionGroups.map((group) => {
        const groupPerms = group.permissions.map((p) => p.id);
        const selectedCount = groupPerms.filter((p) =>
          selectedSet.has(p),
        ).length;
        const allSelected = selectedCount === groupPerms.length;
        const someSelected = selectedCount > 0;

        return (
          <div
            key={group.resource}
            className={clsx(
              "rounded-lg border transition-colors",
              someSelected
                ? "border-adara-200 bg-adara-50/40 dark:border-adara-700 dark:bg-adara-900/20"
                : "border-edge bg-panel",
            )}
          >
            {/* Resource header */}
            <div className="flex items-center justify-between px-4 py-3">
              <Checkbox
                checked={allSelected}
                onChange={() => {
                  toggleRow(group);
                }}
                disabled={disabled}
                label={group.label}
                className={clsx(
                  "[&_label]:font-medium [&_label]:text-main",
                  !allSelected && someSelected && "[&_input]:opacity-70",
                )}
              />
              {someSelected && (
                <span className="text-xs font-medium text-adara-600">
                  {selectedCount}/{groupPerms.length}
                </span>
              )}
            </div>

            {/* Action chips */}
            <div className="flex flex-wrap gap-2 border-t border-edge-soft px-4 py-3">
              {actions.map((action) => {
                const perm = group.permissions.find((p) => p.action === action);
                if (!perm) {
                  return null;
                }
                const isSelected = selectedSet.has(perm.id);
                return (
                  <button
                    key={action}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      toggle(perm.id);
                    }}
                    className={clsx(
                      "rounded-md border px-3 py-1.5 text-xs font-medium transition-all",
                      "focus:outline-none focus:ring-2 focus:ring-adara-500 focus:ring-offset-1",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                      isSelected
                        ? "border-adara-300 bg-adara-700 text-white shadow-sm"
                        : "border-edge bg-panel text-sub hover:border-edge-hard hover:bg-subtle",
                    )}
                    aria-pressed={isSelected}
                    aria-label={perm.label}
                  >
                    {actionLabels[action]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
